import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Settings as SettingsIcon, Link as LinkIcon, RefreshCw, Plus, Copy, Check,
  Power, PowerOff, Trash2, Cloud, ShieldAlert, AlertCircle, FileCode, FileDown,
  Zap, ArrowRight, ArrowLeft, Send, Sparkles,
} from 'lucide-react';
import { cn } from '../lib/utils';
import WingmanBotsSection from '../components/settings/WingmanBotsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const PUBLIC_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/post`;

export default function Settings() {
  const [tab, setTab] = useState('posting');
  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-neutral-500">
        <span className="text-neutral-400">Admin</span>
        <span className="text-neutral-300">·</span>
        <span className="text-[hsl(var(--kf-gold-deep))]">Settings</span>
      </div>
      <div>
        <h2 className="display text-[28px] font-bold text-neutral-900 dark:text-white tracking-tight leading-none inline-flex items-center gap-3">
          <SettingsIcon className="h-6 w-6 text-neutral-500" />
          Settings
        </h2>
        <p className="text-[13px] text-neutral-500 mt-2 max-w-[680px]">
          Admin-only controls for inbound lead capture and downstream integrations.
        </p>
      </div>

      <div className="inline-flex items-center rounded-xl border hairline bg-white dark:bg-neutral-950 p-1" data-testid="settings-tabs">
        {[
          { key: 'posting',   label: 'Posting URLs', Icon: LinkIcon },
          { key: 'fieldspec', label: 'Field Spec',   Icon: FileCode },
          { key: 'arive',     label: 'ARIVE',        Icon: Cloud    },
          { key: 'wingman',   label: 'Wingman Bots', Icon: Sparkles },
        ].map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`settings-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={cn(
                'h-9 px-4 rounded-lg text-[12.5px] font-semibold inline-flex items-center gap-2 transition',
                active ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900' : 'text-neutral-600 hover:bg-neutral-50',
              )}
            >
              <t.Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'posting'   && <PostingUrlsSection />}
      {tab === 'fieldspec' && <FieldSpecSection />}
      {tab === 'arive'     && <AriveSection />}
      {tab === 'wingman'   && <WingmanBotsSection />}
    </div>
  );
}

/* ============================================================ */
/* Posting URLs                                                  */
/* ============================================================ */
function PostingUrlsSection() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/posting-sources`);
      setSources(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const createSource = async () => {
    if (!newName.trim()) { toast.error('Source name required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/posting-sources`, { name: newName.trim() });
      toast.success('Lead source created — URL generated');
      setNewName('');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create source');
    } finally {
      setCreating(false);
    }
  };

  const rotate = async (id) => {
    if (!window.confirm('Rotating the token invalidates the current URL. Vendors will need the new URL. Continue?')) return;
    try {
      await axios.post(`${API}/posting-sources/${id}/rotate-token`);
      toast.success('Token rotated — share the new URL with the vendor');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotate failed');
    }
  };

  const toggleActive = async (s) => {
    try {
      await axios.patch(`${API}/posting-sources/${s.id}`, { active: !s.active });
      toast.success(s.active ? 'Posting URL deactivated' : 'Posting URL activated');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/posting-sources/${s.id}`);
      toast.success('Source deleted');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const totalReceived = useMemo(() => sources.reduce((s, r) => s + (r.total_received || 0), 0), [sources]);

  return (
    <section className="space-y-5" data-testid="posting-urls-section">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Stat label="Active Sources"  value={sources.filter((s) => s.active).length} />
        <Stat label="Total Sources"   value={sources.length} />
        <Stat label="Leads Received"  value={totalReceived.toLocaleString('en-US')} accent="emerald" />
      </div>

      <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 p-5" data-testid="posting-create-row">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-neutral-500">Add a custom lead source</label>
            <input
              data-testid="posting-new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createSource(); }}
              placeholder="e.g. Bankrate Lead Buy"
              className="mt-1.5 w-full h-11 px-3 rounded-md bg-white dark:bg-neutral-900 border hairline text-[14px] font-semibold text-neutral-900 dark:text-white placeholder:font-normal placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 transition"
            />
            <div className="text-[11px] text-neutral-500 mt-1">A fresh posting URL is generated automatically for every new source.</div>
          </div>
          <button
            data-testid="posting-create-btn"
            onClick={createSource}
            disabled={creating || !newName.trim()}
            className={cn(
              'h-11 px-4 rounded-md text-[13px] font-bold inline-flex items-center gap-2 transition',
              creating || !newName.trim()
                ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                : 'bg-neutral-900 text-[hsl(var(--kf-gold))] hover:bg-neutral-800',
            )}
          >
            <Plus className="h-4 w-4" /> Create source
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-[12px] text-neutral-500 mono animate-pulse">Loading sources…</div>
      ) : (
        <div className="space-y-3" data-testid="posting-list">
          {sources.map((s) => (
            <SourceCard key={s.id} source={s} onRotate={rotate} onToggle={toggleActive} onDelete={remove} />
          ))}
        </div>
      )}
    </section>
  );
}

function SourceCard({ source, onRotate, onToggle, onDelete }) {
  const [copied, setCopied] = useState(false);
  const url = `${PUBLIC_BASE}/${source.token}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Copy blocked by browser');
    }
  };
  const last = source.last_received_at
    ? new Date(source.last_received_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '—';
  return (
    <div
      data-testid={`source-card-${source.id}`}
      className={cn(
        'rounded-2xl border hairline bg-white dark:bg-neutral-950 px-5 py-4 transition',
        !source.active && 'opacity-60',
      )}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="display text-[16px] font-bold text-neutral-900 dark:text-white tracking-tight">{source.name}</h3>
            {source.is_default && <span className="text-[9.5px] uppercase tracking-wider font-bold text-neutral-500 border hairline rounded-full px-2 py-0.5">Default</span>}
            {!source.active && <span className="text-[9.5px] uppercase tracking-wider font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">Inactive</span>}
          </div>
          <div className="text-[11.5px] text-neutral-500 mt-1 tabular-nums">
            <span className="font-semibold text-neutral-700 dark:text-neutral-300">{source.total_received.toLocaleString('en-US')}</span> received
            <span className="mx-2 text-neutral-300">·</span>
            Last: <span className="tabular-nums">{last}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid={`source-toggle-${source.id}`}
            onClick={() => onToggle(source)}
            className="h-8 px-3 rounded-md text-[11.5px] font-bold inline-flex items-center gap-1.5 border hairline hover:bg-neutral-50 transition"
            title={source.active ? 'Deactivate' : 'Activate'}
          >
            {source.active ? <PowerOff className="h-3.5 w-3.5 text-rose-600" /> : <Power className="h-3.5 w-3.5 text-emerald-600" />}
            {source.active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            data-testid={`source-rotate-${source.id}`}
            onClick={() => onRotate(source.id)}
            className="h-8 px-3 rounded-md text-[11.5px] font-bold inline-flex items-center gap-1.5 border hairline hover:bg-neutral-50 transition"
            title="Rotate token"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Rotate
          </button>
          {!source.is_default && (
            <button
              data-testid={`source-delete-${source.id}`}
              onClick={() => onDelete(source)}
              className="h-8 px-3 rounded-md text-[11.5px] font-bold inline-flex items-center gap-1.5 border border-rose-200 text-rose-600 hover:bg-rose-50 transition"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <code className="flex-1 min-w-0 truncate text-[12.5px] mono bg-neutral-50 dark:bg-neutral-900 border hairline rounded-md px-3 py-2 text-neutral-700 dark:text-neutral-300">
          {url}
        </code>
        <button
          data-testid={`source-copy-${source.id}`}
          onClick={copy}
          className={cn(
            'h-9 px-3 rounded-md text-[12px] font-bold inline-flex items-center gap-1.5 transition',
            copied ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-neutral-900 text-[hsl(var(--kf-gold))] hover:bg-neutral-800',
          )}
        >
          {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border hairline bg-white dark:bg-neutral-950 px-4 py-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-500">{label}</div>
      <div className={cn(
        'display text-[26px] font-bold tabular-nums mt-1',
        accent === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-900 dark:text-white',
      )}>{value}</div>
    </div>
  );
}

/* ============================================================ */
/* Field Spec — vendor-facing API docs for the posting URL       */
/* ============================================================ */

// Canonical field reference. Order matches priority. Add new fields here.
// `maps_to` = the exact storage path on the lead document, so vendors and
// internal devs can trace where each field surfaces in the CRM.
const FIELDS = [
  // --- Identity ----------------------------------------------------------
  { key: 'loan_tracking_id', aka: 'reference_id · referenceId', type: 'string',  required: 'recommended', example: 'CONV-2026-04-19-001', maps_to: 'lead.referenceId',
    desc: 'Unique tracking ID for the loan across systems. Used to de-dupe + LO claim, and to join updates back from ARIVE/Zapier. Auto-generated (KF-XXXXXXXX) if omitted.' },
  { key: 'first_name',   aka: null,                    type: 'string',  required: 'recommended', example: 'Jane', maps_to: 'lead.application.firstName',
    desc: 'Borrower first name. If you only have a single `name` field, send that instead.' },
  { key: 'last_name',    aka: null,                    type: 'string',  required: 'recommended', example: 'Doe', maps_to: 'lead.application.lastName',
    desc: 'Borrower last name.' },
  { key: 'name',         aka: null,                    type: 'string',  required: 'optional',    example: 'Jane Doe', maps_to: 'lead.name',
    desc: 'Full name. Use ONLY if you can\u2019t split first/last.' },
  { key: 'phone',        aka: null,                    type: 'string',  required: 'recommended', example: '(512) 555-0100', maps_to: 'lead.phone',
    desc: 'Best contact phone. Digits only or (XXX) XXX-XXXX both work.' },
  { key: 'email',        aka: null,                    type: 'string',  required: 'optional',    example: 'jane@example.com', maps_to: 'lead.email',
    desc: 'Borrower email. Optional but improves LO outreach.' },
  { key: 'dob',          aka: 'date_of_birth',         type: 'string',  required: 'optional',    example: '08/14/1985', maps_to: 'lead.application.dob',
    desc: 'Date of birth — MM/DD/YYYY. Matches the format used inside Loan Profile.' },
  { key: 'ssn_last_4',   aka: null,                    type: 'string',  required: 'optional',    example: '4321', maps_to: 'lead.application.ssnLast4',
    desc: 'Last 4 of SSN only. We never accept full SSN over the posting URL.' },

  // --- Property ----------------------------------------------------------
  { key: 'address',      aka: null,                    type: 'string',  required: 'optional',    example: '123 Lakeview Dr', maps_to: 'lead.application.propertyAddress',
    desc: 'Subject property street address.' },
  { key: 'city',         aka: null,                    type: 'string',  required: 'optional',    example: 'Austin', maps_to: 'lead.application.propertyCity',
    desc: 'City of subject property.' },
  { key: 'state',        aka: null,                    type: 'string',  required: 'optional',    example: 'TX', maps_to: 'lead.application.propertyState',
    desc: '2-letter state code. Lower-case accepted; we uppercase server-side.' },
  { key: 'zip',          aka: null,                    type: 'string',  required: 'optional',    example: '78701', maps_to: 'lead.application.propertyZip',
    desc: '5-digit US zip. ZIP+4 also accepted.' },
  { key: 'property_type', aka: null,                   type: 'string',  required: 'optional',    example: 'SFR', maps_to: 'lead.application.propertyType',
    desc: 'SFR | Condo | Multi-Family | Manufactured | Townhome' },
  { key: 'occupancy',    aka: null,                    type: 'string',  required: 'optional',    example: 'Primary', maps_to: 'lead.application.occupancy',
    desc: 'Primary | Secondary | Investment' },

  // --- Loan info ---------------------------------------------------------
  { key: 'loan_amount',  aka: null,                    type: 'number',  required: 'recommended', example: '425000', maps_to: 'lead.application.loanAmount + lead.estAmount',
    desc: 'Estimated loan amount in USD. Send as a number — no commas, no $ sign.' },
  { key: 'loan_purpose', aka: null,                    type: 'string',  required: 'recommended', example: 'Purchase', maps_to: 'lead.application.loanPurpose + lead.loanIntent',
    desc: 'Purchase | Refinance R&T | Cash Out Refinance | HELOC | Home Equity Loan | Investment' },
  { key: 'loan_program', aka: 'loan_type',             type: 'string',  required: 'recommended', example: 'Conventional', maps_to: 'lead.application.loanType',
    desc: 'FHA | VA | Conventional | USDA. Drives Reports → Lead Source loan-type bucket.' },
  { key: 'rate',         aka: 'current_rate',          type: 'number',  required: 'optional',    example: '6.875', maps_to: 'lead.application.rate',
    desc: 'Quoted rate from the borrower\u2019s current loan (refi only). 3 decimal places.' },
  { key: 'current_loan_balance', aka: null,            type: 'number',  required: 'optional',    example: '318500', maps_to: 'lead.application.currentLoanBalance',
    desc: 'Current outstanding mortgage balance (refi only). No commas / no $ sign.' },
  { key: 'credit_score', aka: 'fico',                  type: 'number',  required: 'optional',    example: '742', maps_to: 'lead.application.creditScore + lead.score',
    desc: 'Borrower FICO (300\u2013850). Also seeds the CRM lead-quality score.' },
  { key: 'annual_income', aka: null,                   type: 'number',  required: 'optional',    example: '125000', maps_to: 'lead.application.annualIncome',
    desc: 'Borrower gross annual income in USD. Legacy field — prefer the monthly fields below. We divide by 12 if only this is sent.' },
  { key: 'monthly_base_income', aka: null,             type: 'number',  required: 'optional',    example: '10500', maps_to: 'lead.application.monthlyBaseIncome',
    desc: 'Borrower gross monthly base salary. The CRM stores income monthly — this is the source of truth.' },
  { key: 'monthly_overtime', aka: null,                type: 'number',  required: 'optional',    example: '750', maps_to: 'lead.application.monthlyOvertime',
    desc: 'Average monthly overtime pay.' },
  { key: 'monthly_bonus', aka: null,                   type: 'number',  required: 'optional',    example: '500', maps_to: 'lead.application.monthlyBonus',
    desc: 'Average monthly bonus (annualized / 12 if paid annually).' },
  { key: 'monthly_commission', aka: null,              type: 'number',  required: 'optional',    example: '1200', maps_to: 'lead.application.monthlyCommission',
    desc: 'Average monthly commission. For 1099 / commission-paid borrowers.' },
  { key: 'monthly_other_income', aka: null,            type: 'number',  required: 'optional',    example: '300', maps_to: 'lead.application.monthlyOther',
    desc: 'Other recurring monthly income — rental, child support, alimony, etc.' },
  { key: 'employer',     aka: null,                    type: 'string',  required: 'optional',    example: 'Stripe Inc.', maps_to: 'lead.application.employer',
    desc: 'Current employer (company name).' },
  { key: 'position',     aka: null,                    type: 'string',  required: 'optional',    example: 'Engineering Lead', maps_to: 'lead.application.position',
    desc: 'Job title / role at current employer.' },
  { key: 'employment_type', aka: null,                 type: 'string',  required: 'optional',    example: 'W2 Employed', maps_to: 'lead.application.employmentType',
    desc: 'W2 Employed | Self Employed | 1099 Contractor | Retired | Other.' },
  { key: 'employment_start', aka: null,                type: 'string',  required: 'optional',    example: '03/15/2019', maps_to: 'lead.application.employmentStart',
    desc: 'Start date at current employer — MM/DD/YYYY.' },
  { key: 'veteran_status', aka: null,                  type: 'string',  required: 'optional',    example: 'Veteran', maps_to: 'lead.application.veteranStatus',
    desc: 'Veteran | Active Duty | Spouse | None. Powers VA-eligibility flagging.' },

  // --- Attribution / metadata -------------------------------------------
  { key: 'campaign',     aka: null,                    type: 'string',  required: 'optional',    example: 'Conv-Mar-2026', maps_to: 'lead.mailer.campaign',
    desc: 'Campaign tag. Powers per-campaign reporting in Mailer Dashboard.' },
  { key: 'vendor',       aka: null,                    type: 'string',  required: 'optional',    example: 'BankrateLeadCo', maps_to: 'lead.mailer.vendor',
    desc: 'Your own vendor identifier — stored verbatim for attribution.' },
  { key: 'utm_source',   aka: null,                    type: 'string',  required: 'optional',    example: 'google', maps_to: 'lead.mailer.utm.source',
    desc: 'Web attribution. Saved under mailer.utm for later analytics joins.' },
  { key: 'utm_medium',   aka: null,                    type: 'string',  required: 'optional',    example: 'cpc', maps_to: 'lead.mailer.utm.medium',
    desc: 'Web attribution (paid / organic / email / referral).' },
  { key: 'utm_campaign', aka: null,                    type: 'string',  required: 'optional',    example: 'spring-refi', maps_to: 'lead.mailer.utm.campaign',
    desc: 'Web attribution — campaign string from the ad/email link.' },
];

function requiredBadgeStyle(level) {
  if (level === 'recommended') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-neutral-100 text-neutral-500 border-neutral-200';
}

function FieldSpecSection() {
  const [copiedJson, setCopiedJson] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);

  const exampleUrl = `${PUBLIC_BASE}/<YOUR_TOKEN>`;
  const exampleJson = useMemo(() => {
    const obj = {};
    FIELDS.filter((f) => f.required === 'recommended').forEach((f) => {
      obj[f.key] = f.type === 'number' ? Number(f.example) : f.example;
    });
    return JSON.stringify(obj, null, 2);
  }, []);

  const exampleCurl = `curl -X POST ${exampleUrl} \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(JSON.parse(exampleJson))}'`;

  const copy = async (text, setFlag) => {
    try {
      await navigator.clipboard.writeText(text);
      setFlag(true);
      setTimeout(() => setFlag(false), 1600);
    } catch {
      toast.error('Copy blocked by browser');
    }
  };

  // PDF generation — opens a clean print-ready HTML document in a new
  // window and triggers the print dialog there. User picks "Save as PDF"
  // in the dialog. Zero JS deps, no visibility-cascade footguns, the new
  // window has no app chrome to hide.
  const downloadPdf = () => {
    const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const rows = FIELDS.map((f, i) => `
      <tr style="background:${i % 2 ? '#fafafa' : '#fff'}">
        <td>
          <code><strong>${escapeHtml(f.key)}</strong></code>
          ${f.aka ? `<div class="aka">alias: <code>${escapeHtml(f.aka)}</code></div>` : ''}
        </td>
        <td><code class="muted">${escapeHtml(f.type)}</code></td>
        <td class="center">${f.required === 'recommended' ? '<span class="star">★</span>' : '<span class="dot">·</span>'}</td>
        <td><code>${escapeHtml(f.example)}</code></td>
        <td><code class="path">${escapeHtml(f.maps_to)}</code></td>
        <td>${escapeHtml(f.desc)}</td>
      </tr>
    `).join('');

    const html = `<!doctype html>
<html><head><meta charset="utf-8" />
<title>Kinfolk Lead Posting API — Field Reference</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
    color: #0a0a0a;
    margin: 0;
    padding: 28px 32px;
    max-width: 880px;
  }
  header { border-bottom: 2px solid #0a0a0a; padding-bottom: 12px; margin-bottom: 22px; }
  .crumb { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; font-weight: 700; color: #666; }
  h1 { margin: 4px 0 0; font-size: 24px; font-weight: 700; letter-spacing: -0.01em; }
  .gen { font-size: 11px; color: #666; margin-top: 6px; }
  .intro { font-size: 11.5px; line-height: 1.55; margin-bottom: 18px; }
  .endpoint { background: #f4f4f5; padding: 4px 8px; border-radius: 3px; font-family: ui-monospace, "SF Mono", monospace; font-size: 10.5px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; margin: 16px 0 22px; }
  th { background: #0a0a0a; color: #fff; font-size: 9.5px; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 700; padding: 7px 8px; text-align: left; border: 1px solid #0a0a0a; }
  td { padding: 5px 8px; vertical-align: top; line-height: 1.4; border: 1px solid #e5e5e5; font-size: 10px; }
  td.center { text-align: center; }
  code { font-family: ui-monospace, "SF Mono", monospace; }
  code.path { color: #047857; }
  code.muted { color: #666; }
  .aka { font-size: 8.5px; color: #777; margin-top: 2px; }
  .star { color: #b45309; font-weight: 700; }
  .dot { color: #999; }
  h3 { font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; font-weight: 700; color: #666; margin: 14px 0 6px; }
  pre { background: #f4f4f5; padding: 10px 12px; border-radius: 4px; font-size: 10px; font-family: ui-monospace, monospace; white-space: pre-wrap; line-height: 1.5; margin: 0 0 14px; border: 1px solid #e5e5e5; }
  footer { border-top: 1px solid #e5e5e5; margin-top: 18px; padding-top: 10px; font-size: 9px; color: #888; }
  @media print {
    body { padding: 0; }
    @page { margin: 0.5in; }
  }
</style>
</head><body>
  <header>
    <div class="crumb">Kinfolk Home Loans</div>
    <h1>Lead Posting API — Field Reference</h1>
    <div class="gen">Generated ${escapeHtml(today)} &middot; v1.0</div>
  </header>
  <p class="intro">
    POST leads to your unique URL at <span class="endpoint">${escapeHtml(exampleUrl)}</span>.
    Content-Type <code>application/json</code>. Token in path is the only auth. Unknown fields
    are accepted silently and stored on the lead document. <strong>★</strong> indicates recommended fields.
  </p>

  <table>
    <thead>
      <tr>
        <th>Field</th><th>Type</th><th>Req</th><th>Example</th><th>CRM Field</th><th>Notes</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <h3>Example payload</h3>
  <pre>${escapeHtml(exampleJson)}</pre>

  <h3>Success response (200 OK)</h3>
  <pre>{
  "ok": true,
  "lead_id": "f9c3...e6a1",
  "reference_id": "CONV-2026-04-19-001",
  "source": "Conventional Mailer"
}</pre>

  <h3>Error responses</h3>
  <pre>404 Invalid posting URL              token doesn't match any source
403 This posting URL has been deactivated  admin paused this source
422 Unprocessable Entity             payload could not be parsed as JSON</pre>

  <footer>
    Kinfolk Home Loans &middot; Confidential — for integration partner use only. Questions: your assigned LO.
  </footer>

  <script>
    window.addEventListener('load', function() {
      setTimeout(function() { window.print(); }, 150);
    });
  </script>
</body></html>`;

    const win = window.open('', '_blank', 'width=900,height=1100');
    if (!win) {
      toast.error('Popup blocked. Allow popups for this site and try again.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  return (
    <section className="space-y-5" data-testid="fieldspec-section">
      {/* Intro */}
      <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="display text-[16px] font-bold text-neutral-900 dark:text-white tracking-tight inline-flex items-center gap-2">
              <FileCode className="h-4 w-4 text-neutral-500" />
              API Field Reference
            </h3>
            <p className="text-[12.5px] text-neutral-600 dark:text-neutral-300 mt-2 leading-snug max-w-[860px]">
              Hand this page to any vendor or in-house developer who needs to POST leads into Kinfolk. Every endpoint accepts
              <code className="mono mx-1 px-1 rounded bg-neutral-100 dark:bg-white/[0.06] text-[11.5px]">application/json</code>
              with the fields below. Unknown fields are accepted silently and stored on the lead document &mdash; we never reject
              a payload for sending too much, only too little.
            </p>
          </div>
          <button
            data-testid="fieldspec-download-pdf"
            onClick={downloadPdf}
            className="h-10 px-4 rounded-md text-[12.5px] font-bold inline-flex items-center gap-2 bg-neutral-900 text-[hsl(var(--kf-gold))] hover:bg-neutral-800 transition shrink-0"
          >
            <FileDown className="h-3.5 w-3.5" /> Download PDF
          </button>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-[11.5px]">
          <SpecPill label="Method"       value="POST" />
          <SpecPill label="Auth"         value="Token in URL path" />
          <SpecPill label="Content-Type" value="application/json" />
        </div>
      </div>

      {/* Endpoint block */}
      <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-neutral-500">Endpoint</div>
        <code className="block w-full text-[12.5px] mono bg-neutral-50 dark:bg-neutral-900 border hairline rounded-md px-3 py-2 text-neutral-700 dark:text-neutral-300 break-all">
          POST {exampleUrl}
        </code>
        <p className="text-[11.5px] text-neutral-500">Replace <code className="mono">{'<YOUR_TOKEN>'}</code> with the token from any active Posting URL on the previous tab.</p>
      </div>

      {/* Field table */}
      <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 overflow-hidden" data-testid="fieldspec-table">
        <div
          className="grid gap-3 px-5 py-2.5 text-[9.5px] uppercase tracking-wider font-bold text-neutral-500 dark:text-neutral-400 border-b hairline bg-neutral-50/50 dark:bg-neutral-900/50"
          style={{ gridTemplateColumns: '170px 70px 100px 140px 220px minmax(220px,1.2fr)' }}
        >
          <div>Field</div>
          <div>Type</div>
          <div>Required</div>
          <div>Example</div>
          <div>CRM Field</div>
          <div>Notes</div>
        </div>
        <div className="divide-y divide-neutral-100 dark:divide-white/[0.05]">
          {FIELDS.map((f) => (
            <div
              key={f.key}
              data-testid={`fieldspec-row-${f.key}`}
              className="grid gap-3 px-5 py-2.5 items-start text-[12.5px]"
              style={{ gridTemplateColumns: '170px 70px 100px 140px 220px minmax(220px,1.2fr)' }}
            >
              <div className="min-w-0">
                <code className="mono font-bold text-neutral-900 dark:text-white">{f.key}</code>
                {f.aka && (
                  <div className="text-[10.5px] text-neutral-500 mt-0.5">
                    alias: <code className="mono">{f.aka}</code>
                  </div>
                )}
              </div>
              <div className="text-neutral-500 mono text-[11.5px] pt-0.5">{f.type}</div>
              <div className="pt-0.5">
                <span className={cn('inline-flex text-[10px] uppercase tracking-wider font-bold border rounded-full px-2 py-0.5', requiredBadgeStyle(f.required))}>
                  {f.required}
                </span>
              </div>
              <div className="text-neutral-700 dark:text-neutral-300 mono text-[11.5px] break-all pt-0.5">{f.example}</div>
              <div className="text-emerald-700 dark:text-emerald-400 mono text-[11px] break-all pt-0.5">{f.maps_to}</div>
              <div className="text-neutral-600 dark:text-neutral-400 leading-snug pt-0.5">{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Two example panes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Example JSON */}
        <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 border-b hairline bg-neutral-50/40 dark:bg-neutral-900/40">
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-neutral-500">Example JSON body</div>
            <button
              data-testid="fieldspec-copy-json"
              onClick={() => copy(exampleJson, setCopiedJson)}
              className={cn(
                'h-7 px-2.5 rounded text-[11px] font-bold inline-flex items-center gap-1.5 transition',
                copiedJson ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-neutral-900 text-[hsl(var(--kf-gold))] hover:bg-neutral-800',
              )}
            >
              {copiedJson ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
            </button>
          </div>
          <pre className="mono text-[11.5px] leading-relaxed text-neutral-700 dark:text-neutral-300 p-5 overflow-x-auto whitespace-pre">{exampleJson}</pre>
        </div>
        {/* Example cURL */}
        <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 border-b hairline bg-neutral-50/40 dark:bg-neutral-900/40">
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-neutral-500">Example cURL</div>
            <button
              data-testid="fieldspec-copy-curl"
              onClick={() => copy(exampleCurl, setCopiedCurl)}
              className={cn(
                'h-7 px-2.5 rounded text-[11px] font-bold inline-flex items-center gap-1.5 transition',
                copiedCurl ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-neutral-900 text-[hsl(var(--kf-gold))] hover:bg-neutral-800',
              )}
            >
              {copiedCurl ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
            </button>
          </div>
          <pre className="mono text-[11.5px] leading-relaxed text-neutral-700 dark:text-neutral-300 p-5 overflow-x-auto whitespace-pre">{exampleCurl}</pre>
        </div>
      </div>

      {/* Response shape */}
      <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 p-5">
        <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-neutral-500">Success response (200 OK)</div>
        <pre className="mono text-[11.5px] leading-relaxed text-neutral-700 dark:text-neutral-300 mt-3 bg-neutral-50 dark:bg-neutral-900 border hairline rounded-md px-3 py-2 overflow-x-auto">
{`{
  "ok": true,
  "lead_id": "f9c3...e6a1",
  "reference_id": "CONV-2026-04-19-001",
  "source": "Conventional Mailer"
}`}
        </pre>
        <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-neutral-500 mt-5">Error responses</div>
        <ul className="mt-2 space-y-1.5 text-[12px] text-neutral-700 dark:text-neutral-300">
          <li><code className="mono">404 Invalid posting URL</code> — token doesn&rsquo;t match any source.</li>
          <li><code className="mono">403 This posting URL has been deactivated</code> — admin paused this source.</li>
          <li><code className="mono">422 Unprocessable Entity</code> — payload could not be parsed as JSON.</li>
        </ul>
      </div>

      {/* ============================================================ */}
      {/* OUTBOUND — Push payload sent to Zapier / ARIVE                */}
      {/* ============================================================ */}
      <OutboundPushSpec />
    </section>
  );
}

/* Reference of every field we POST to the configured Zapier webhook when
   an admin clicks "Send to Arive" on a Lead Profile. Maps each outbound
   key back to the lead document path so the LO knows what to fill in
   inside the CRM to populate each ARIVE field. */
const PUSH_FIELDS = [
  // Identity
  { key: 'crm_lead_id',     type: 'string', from: 'lead.id',                      desc: 'Internal CRM lead UUID. Useful if ARIVE wants to store a back-link.' },
  { key: 'loan_tracking_id', type: 'string', from: 'lead.referenceId',           desc: 'Unique tracking ID for the loan. Auto-generated KF-XXXXXXXX (or mailer ref). ⭐ The join key for the pull-back flow — map this to an ARIVE External ID / CRM ID / Custom field.' },
  { key: 'reference_id',    type: 'string', from: 'lead.referenceId',             desc: 'Alias for loan_tracking_id — kept for backwards compatibility. Same value.' },
  { key: 'name',            type: 'string', from: 'lead.name',                    desc: 'Full borrower name as displayed in the CRM.' },
  { key: 'first_name',      type: 'string', from: 'lead.application.firstName',   desc: 'Borrower first name.' },
  { key: 'last_name',       type: 'string', from: 'lead.application.lastName',    desc: 'Borrower last name.' },
  { key: 'phone',           type: 'string', from: 'lead.phone',                   desc: 'Best contact phone.' },
  { key: 'email',           type: 'string', from: 'lead.email',                   desc: 'Borrower email.' },
  { key: 'dob',             type: 'string', from: 'lead.application.dob',         desc: 'Date of birth — MM/DD/YYYY.' },
  { key: 'ssn_last_4',      type: 'string', from: 'lead.application.ssnLast4',    desc: 'Last 4 of SSN only.' },
  // Property
  { key: 'property_address', type: 'string', from: 'lead.application.propertyAddress', desc: 'Subject property street address.' },
  { key: 'property_city',    type: 'string', from: 'lead.application.propertyCity',    desc: 'Subject property city.' },
  { key: 'property_state',   type: 'string', from: 'lead.application.propertyState',   desc: '2-letter state code.' },
  { key: 'property_zip',     type: 'string', from: 'lead.application.propertyZip',     desc: '5-digit US zip.' },
  { key: 'property_type',    type: 'string', from: 'lead.application.propertyType',    desc: 'SFR | Condo | Multi-Family | Manufactured | Townhome.' },
  { key: 'occupancy',        type: 'string', from: 'lead.application.occupancy',       desc: 'Primary | Secondary | Investment.' },
  // Loan
  { key: 'loan_purpose',     type: 'string', from: 'lead.application.loanPurpose',     desc: 'Purchase | Refinance R&T | Cash Out Refinance | HELOC | Home Equity Loan | Investment.' },
  { key: 'loan_type',        type: 'string', from: 'lead.application.loanType',        desc: 'FHA | VA | Conventional | USDA. Use this for ARIVE\u2019s Mortgage Type field.' },
  { key: 'loan_amount',      type: 'number', from: 'lead.application.loanAmount',      desc: 'Loan amount in USD. Use for ARIVE\u2019s Base Loan Amount field.' },
  { key: 'rate',             type: 'number', from: 'lead.application.rate',            desc: 'Quoted / current note rate. Map to ARIVE\u2019s Note Rate.' },
  { key: 'loan_term_months', type: 'number', from: 'lead.application.loanTerm',        desc: 'Loan term in months (360 = 30yr). Defaults to 360 if unset.' },
  { key: 'loan_term_years',  type: 'number', from: 'computed',                         desc: 'Loan term in years (30, 20, 15, 10). Derived from loan_term_months.' },
  { key: 'amortization_type', type: 'string', from: 'lead.application.amortizationType', desc: 'Fixed | ARM. Defaults to "Fixed" if unset.' },
  { key: 'interest_only',    type: 'boolean', from: 'lead.application.interestOnly',   desc: 'Whether the loan is interest-only. Defaults to false.' },
  { key: 'lien_position',    type: 'string', from: 'lead.application.lienPosition',    desc: 'First | Second. Defaults to "First".' },
  { key: 'property_value',   type: 'number', from: 'lead.application.propertyValue',   desc: 'Estimated property value. Falls back to loan_amount if unset. Map to ARIVE\u2019s purchasePriceOrEstimatedValue.' },
  { key: 'purchase_price',   type: 'number', from: 'lead.application.purchasePrice',   desc: 'Purchase price (purchase loans only). Falls back to property_value → loan_amount.' },
  { key: 'taxes',            type: 'number', from: 'lead.application.taxes',           desc: 'Annual property taxes in USD.' },
  { key: 'insurance',        type: 'number', from: 'lead.application.insurance',       desc: 'Annual homeowner\u2019s insurance in USD.' },
  { key: 'hoa',              type: 'number', from: 'lead.application.hoa',             desc: 'Monthly HOA dues in USD.' },
  { key: 'current_loan_balance', type: 'number', from: 'lead.application.currentLoanBalance', desc: 'Refi only — current outstanding mortgage balance.' },
  { key: 'credit_score',     type: 'number', from: 'lead.application.creditScore',     desc: 'Borrower FICO (300\u2013850).' },
  // Employment
  { key: 'employer',         type: 'string', from: 'lead.application.employer',        desc: 'Current employer (company name).' },
  { key: 'position',         type: 'string', from: 'lead.application.position',        desc: 'Job title / role.' },
  { key: 'employment_type',  type: 'string', from: 'lead.application.employmentType',  desc: 'W2 Employed | Self Employed | 1099 Contractor | Retired | Other.' },
  { key: 'employment_start', type: 'string', from: 'lead.application.employmentStart', desc: 'Start date at current employer — MM/DD/YYYY.' },
  // Income — monthly is source of truth, annual is derived (× 12)
  { key: 'monthly_base_income',  type: 'number', from: 'lead.application.monthlyBaseIncome',  desc: 'Gross monthly base salary.' },
  { key: 'monthly_overtime',     type: 'number', from: 'lead.application.monthlyOvertime',    desc: 'Average monthly overtime.' },
  { key: 'monthly_bonus',        type: 'number', from: 'lead.application.monthlyBonus',       desc: 'Average monthly bonus.' },
  { key: 'monthly_commission',   type: 'number', from: 'lead.application.monthlyCommission',  desc: 'Average monthly commission.' },
  { key: 'monthly_other_income', type: 'number', from: 'lead.application.monthlyOther',       desc: 'Other recurring monthly income.' },
  { key: 'total_monthly_income', type: 'number', from: 'computed',                            desc: 'SUM of all 5 monthly fields. ⭐ Map this to ARIVE\u2019s Total Monthly Income field.' },
  { key: 'annual_income',        type: 'number', from: 'computed',                            desc: 'Derived from total_monthly_income × 12. Use if ARIVE wants annual gross.' },
  { key: 'veteran_status',       type: 'string', from: 'lead.application.veteranStatus',      desc: 'Veteran | Active Duty | Spouse | None.' },
  // Source / attribution
  { key: 'source',          type: 'string', from: 'lead.source',                desc: 'Lead source label (Conv Mailer / Web / Referral / etc.).' },
  { key: 'campaign',        type: 'string', from: 'lead.mailer.campaign',       desc: 'Campaign tag — mailer leads only.' },
  { key: 'vendor',          type: 'string', from: 'lead.mailer.vendor',         desc: 'Vendor identifier — mailer / posting leads only.' },
  { key: 'utm',             type: 'object', from: 'lead.mailer.utm',            desc: 'Web attribution {source, medium, campaign} — web leads only.' },
  { key: 'loan_status',     type: 'string', from: 'lead.loanStatus',            desc: 'Current pipeline status at push time.' },
  { key: 'assigned_to',     type: 'string', from: 'lead.assignedTo',            desc: 'LO user ID who currently owns this lead.' },
  { key: 'arive_loan_id',   type: 'string', from: 'lead.ariveLoanId',           desc: 'Populated on resends after the loan exists in ARIVE.' },
  { key: 'crm_pushed_at',   type: 'string', from: 'computed',                   desc: 'ISO timestamp — when the push fired.' },
];

function OutboundPushSpec() {
  const examplePayload = `{
  "crm_lead_id":          "7adf4d78-2424-45af-9e53-2f96e0090c9d",
  "loan_tracking_id":     "KF-A3F9C7B1",
  "reference_id":         "KF-A3F9C7B1",
  "name":                 "Jose Lewis",
  "first_name":           "Jose",
  "last_name":            "Lewis",
  "phone":                "(736) 855-9745",
  "email":                "jose.lewis@example.com",
  "dob":                  "08/23/1973",
  "ssn_last_4":           "6688",
  "property_address":     "7991 Park Ct",
  "property_city":        "Jacksonville",
  "property_state":       "FL",
  "property_zip":         "21738",
  "property_type":        "Condo",
  "occupancy":            "Primary",
  "loan_purpose":         "Purchase",
  "loan_type":            "Conventional",
  "loan_amount":          425000,
  "rate":                 6.875,
  "loan_term_months":     360,
  "loan_term_years":      30,
  "amortization_type":    "Fixed",
  "interest_only":        false,
  "lien_position":        "First",
  "property_value":       460000,
  "purchase_price":       460000,
  "taxes":                4800,
  "insurance":            1200,
  "hoa":                  0,
  "credit_score":         742,
  "employer":             "Stripe Inc.",
  "position":             "Engineering Lead",
  "employment_type":      "W2 Employed",
  "employment_start":     "03/15/2019",
  "monthly_base_income":  12000,
  "monthly_overtime":     0,
  "monthly_bonus":        2500,
  "monthly_commission":   0,
  "monthly_other_income": 500,
  "total_monthly_income": 15000,
  "annual_income":        180000,
  "veteran_status":       null,
  "source":               "Web Lead",
  "loan_status":          "ready_to_submit",
  "assigned_to":          "user_abc123",
  "arive_loan_id":        null,
  "crm_pushed_at":        "2026-05-28T17:42:30.123456+00:00"
}`;

  return (
    <div className="space-y-4" data-testid="outbound-push-spec">
      <div className="rounded-2xl border hairline bg-emerald-50/40 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-500/30 p-5">
        <div className="flex items-start gap-3">
          <ArrowRight className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <div>
            <h2 className="display text-[20px] font-bold text-neutral-900 dark:text-white tracking-tight">Outbound — Push payload (CRM → ARIVE via Zapier)</h2>
            <p className="text-[12.5px] text-neutral-700 dark:text-neutral-300 mt-2 leading-snug max-w-[860px]">
              Every field below is included in the JSON body sent to your Zapier Catch Hook when an admin clicks <span className="font-semibold">Send to Arive</span> on a Lead Profile. Use this as your reference when mapping fields into ARIVE inside Zapier.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 overflow-hidden">
        <div
          className="grid gap-3 px-5 py-2.5 text-[9.5px] uppercase tracking-wider font-bold text-neutral-500 dark:text-neutral-400 border-b hairline bg-neutral-50/50 dark:bg-neutral-900/50"
          style={{ gridTemplateColumns: '210px 80px 260px minmax(220px,1.2fr)' }}
        >
          <div>Field Name (Zapier)</div>
          <div>Type</div>
          <div>From (CRM Path)</div>
          <div>What to map it to in ARIVE</div>
        </div>
        <div className="divide-y divide-neutral-100 dark:divide-white/[0.05]">
          {PUSH_FIELDS.map((f) => (
            <div
              key={f.key}
              data-testid={`push-spec-row-${f.key}`}
              className="grid gap-3 px-5 py-2.5 items-start text-[12.5px]"
              style={{ gridTemplateColumns: '210px 80px 260px minmax(220px,1.2fr)' }}
            >
              <div className="min-w-0">
                <code className="mono font-bold text-neutral-900 dark:text-white break-all">{f.key}</code>
              </div>
              <div className="text-neutral-500 mono text-[11.5px] pt-0.5">{f.type}</div>
              <div className="text-emerald-700 dark:text-emerald-400 mono text-[11px] break-all pt-0.5">{f.from}</div>
              <div className="text-neutral-600 dark:text-neutral-400 leading-snug pt-0.5">{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b hairline bg-neutral-50/40 dark:bg-neutral-900/40">
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-neutral-500">Example push payload — JSON body</div>
        </div>
        <pre className="mono text-[11.5px] leading-relaxed text-neutral-700 dark:text-neutral-300 p-5 overflow-x-auto whitespace-pre">{examplePayload}</pre>
      </div>
    </div>
  );
}

// Tiny HTML escaper for the printable PDF window — keeps field examples
// + descriptions from breaking the print layout if anyone ever sneaks a
// `<` or `&` into a field spec entry.
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function SpecPill({ label, value }) {
  return (
    <div className="rounded-lg border hairline px-3 py-2 bg-neutral-50/60 dark:bg-neutral-900/40">
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-500">{label}</div>
      <div className="mono text-[12.5px] font-bold text-neutral-900 dark:text-white mt-1">{value}</div>
    </div>
  );
}

/* ============================================================ */
/* ARIVE Integration — via Zapier                                */
/* ============================================================ */
function AriveSection() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ zapier_push_url: '' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [aSource, setASource] = useState(null);   // posting source named "ARIVE Status Sync"
  const [creating, setCreating] = useState(false);

  // Load settings + look for the dedicated status-sync posting source.
  const refresh = async () => {
    const [s, srcs] = await Promise.all([
      axios.get(`${API}/arive/settings`).then((r) => r.data),
      axios.get(`${API}/posting-sources`).then((r) => r.data).catch(() => []),
    ]);
    setSettings(s);
    setForm({ zapier_push_url: '' });
    const match = (srcs || []).find((x) => /arive/i.test(x.name));
    setASource(match || null);
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    if (!form.zapier_push_url || !/^https?:\/\//i.test(form.zapier_push_url.trim())) {
      toast.error('Paste the full Zapier Catch Hook URL (must start with https://).');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/arive/settings`, { zapier_push_url: form.zapier_push_url.trim() });
      setSettings(r.data);
      setForm({ zapier_push_url: '' });
      toast.success('Zapier webhook saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/arive/test-connection`);
      toast.success('Zapier received the test ping. Check your Zap History.');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const createSyncSource = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/posting-sources`, { name: 'ARIVE Status Sync' });
      setASource(r.data);
      toast.success('Posting source created');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create posting source');
    } finally {
      setCreating(false);
    }
  };

  const inboundUrl = aSource
    ? `${PUBLIC_BASE}/${aSource.token}/update/{loan_tracking_id}`
    : null;

  return (
    <section className="space-y-5" data-testid="arive-section">
      {/* How it works banner */}
      <div className="rounded-2xl border border-amber-200 bg-amber-50/60 dark:bg-amber-950/30 dark:border-amber-500/30 px-5 py-4" data-testid="arive-zapier-banner">
        <div className="flex items-start gap-3">
          <Zap className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-[13px] font-bold text-neutral-900 dark:text-white">Connected via Zapier</div>
            <p className="text-[12.5px] text-neutral-700 dark:text-neutral-300 mt-1 leading-snug max-w-[860px]">
              Kinfolk talks to ARIVE through Zapier — no direct API keys required. <span className="font-semibold">Push</span> fires when an LO hits “Send to Arive” on a Lead Profile. <span className="font-semibold">Pull-back</span> happens when your Zap calls our inbound URL on a loan-status change in ARIVE.
            </p>
          </div>
          <StatusPill status={settings?.status || 'not_configured'} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <FlowStep
            n={1}
            arrow="forward"
            title="CRM → ARIVE (push)"
            body="LO clicks Send to Arive → we POST the lead JSON to your Zapier Catch Hook. Your Zap creates the loan in ARIVE."
          />
          <FlowStep
            n={2}
            arrow="back"
            title="ARIVE → CRM (pull-back)"
            body="ARIVE updates a loan → your Zap POSTs to the inbound URL below using the lead's reference ID. We update loanStatus, loan amount, and milestones."
          />
        </div>
      </div>

      {/* ============== PUSH — Zapier webhook URL ============== */}
      <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 p-5 space-y-4" data-testid="arive-push-form">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="display text-[15px] font-bold text-neutral-900 dark:text-white tracking-tight flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-emerald-600" />
              Push to ARIVE — Zapier webhook URL
            </h3>
            <p className="text-[11.5px] text-neutral-500 mt-1">
              In Zapier, create a Zap with trigger <span className="font-semibold">Webhooks by Zapier → Catch Hook</span>. Paste the URL Zapier gives you here.
            </p>
          </div>
        </div>

        {settings?.zapier_push_url ? (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-500/30">
            <Check className="h-4 w-4 text-emerald-600" />
            <span className="text-[12px] text-emerald-900 dark:text-emerald-200 font-semibold flex-1 mono truncate" title={settings.zapier_push_url}>
              {settings.zapier_push_url}
            </span>
            {settings.last_test_at && (
              <span className="text-[10.5px] text-emerald-700 dark:text-emerald-300">
                last tested {fmtAgo(settings.last_test_at)}
              </span>
            )}
          </div>
        ) : null}

        <Field
          label="Zapier Catch Hook URL"
          placeholder="https://hooks.zapier.com/hooks/catch/123456/abc123/"
          value={form.zapier_push_url}
          onChange={(v) => setForm((f) => ({ ...f, zapier_push_url: v }))}
          testid="arive-zapier-url"
        />

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            data-testid="arive-test-connection"
            onClick={test}
            disabled={testing || !settings?.zapier_push_url}
            className="h-10 px-4 rounded-md text-[12.5px] font-bold inline-flex items-center gap-2 border hairline hover:bg-neutral-50 dark:hover:bg-neutral-900 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <Send className="h-3.5 w-3.5" /> {testing ? 'Sending…' : 'Send test ping'}
          </button>
          <button
            data-testid="arive-save"
            onClick={save}
            disabled={saving}
            className={cn(
              'h-10 px-4 rounded-md text-[12.5px] font-bold inline-flex items-center gap-2 transition',
              saving ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed' : 'bg-neutral-900 text-[hsl(var(--kf-gold))] hover:bg-neutral-800',
            )}
          >
            Save webhook URL
          </button>
        </div>
      </div>

      {/* ============== PULL — inbound URL Zapier will POST to ============== */}
      <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 p-5 space-y-4" data-testid="arive-pull-form">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="display text-[15px] font-bold text-neutral-900 dark:text-white tracking-tight flex items-center gap-2">
              <ArrowLeft className="h-4 w-4 text-sky-600" />
              Pull back from ARIVE — inbound URL
            </h3>
            <p className="text-[11.5px] text-neutral-500 mt-1">
              Use this URL inside Zapier's <span className="font-semibold">Webhooks by Zapier → Custom Request (POST)</span> action whenever an ARIVE loan status changes. Replace <code className="mono">{'{loan_tracking_id}'}</code> with the lead's Loan Tracking ID from ARIVE.
            </p>
          </div>
        </div>

        {!aSource ? (
          <div className="flex items-center justify-between gap-3 p-3 rounded-md bg-neutral-50 dark:bg-neutral-900 border hairline">
            <p className="text-[12px] text-neutral-700 dark:text-neutral-300">
              No <span className="font-semibold">ARIVE Status Sync</span> posting source yet. Create one to mint a secure inbound URL.
            </p>
            <button
              data-testid="arive-create-source"
              onClick={createSyncSource}
              disabled={creating}
              className="h-9 px-3 rounded-md text-[12px] font-bold inline-flex items-center gap-1.5 bg-neutral-900 text-[hsl(var(--kf-gold))] hover:bg-neutral-800 disabled:opacity-40"
            >
              <Plus className="h-3.5 w-3.5" /> Create source
            </button>
          </div>
        ) : (
          <>
            <div className="rounded-md bg-neutral-50 dark:bg-neutral-900 border hairline p-3 flex items-center gap-2" data-testid="arive-inbound-url">
              <code className="mono text-[11.5px] text-neutral-800 dark:text-neutral-200 truncate flex-1" title={inboundUrl}>
                {inboundUrl}
              </code>
              <button
                onClick={() => { navigator.clipboard.writeText(inboundUrl); toast.success('Copied'); }}
                className="h-8 px-2.5 rounded-md border hairline text-[11px] font-bold inline-flex items-center gap-1.5 hover:bg-white dark:hover:bg-neutral-800 transition"
              >
                <Copy className="h-3 w-3" /> Copy
              </button>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-neutral-500 mb-1.5">Example JSON body Zapier should send</div>
              <pre className="rounded-md border hairline bg-neutral-900 text-[11.5px] text-emerald-300 p-3 overflow-x-auto mono" data-testid="arive-example-body">
{`{
  "arive_loan_id": "ARV-12345",
  "loan_status":   "approved_with_conditions",
  "loan_amount":   425000,
  "rate":          6.875,
  "closing_date":  "2026-03-15",
  "note":          "AUS run cleared, CD out today"
}`}
              </pre>
              <p className="text-[11px] text-neutral-500 mt-2">
                Accepted <code className="mono">loan_status</code> values: <span className="mono">submitted</span>, <span className="mono">approved_with_conditions</span>, <span className="mono">clear_to_close</span>, <span className="mono">funded</span>, <span className="mono">denied</span>, <span className="mono">withdrawn</span>.
              </p>
            </div>
          </>
        )}
      </div>
      {/* ============== Recent activity feed ============== */}
      <AriveActivityFeed />
    </section>
  );
}

function AriveActivityFeed() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/arive/sync-logs?limit=15`);
      setLogs(r.data || []);
    } catch (_) {}
    finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => {
    load();
    // Soft poll every 20s so admins watching the panel see new events flow in
    // without manually refreshing. Cheap query — capped at 15 rows.
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 overflow-hidden" data-testid="arive-activity-feed">
      <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b hairline">
        <div>
          <h3 className="display text-[15px] font-bold text-neutral-900 dark:text-white tracking-tight">Recent ARIVE Sync Activity</h3>
          <p className="text-[11.5px] text-neutral-500 mt-0.5">Last 15 push/pull events. Auto-refreshes every 20s.</p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="h-8 px-2.5 rounded-md border hairline text-[11px] font-bold inline-flex items-center gap-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition disabled:opacity-50"
          data-testid="arive-refresh-activity"
        >
          <RefreshCw className={cn('h-3 w-3', refreshing && 'animate-spin')} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="px-5 py-10 text-center text-[12px] text-neutral-500">Loading…</div>
      ) : logs.length === 0 ? (
        <div className="px-5 py-10 text-center" data-testid="arive-activity-empty">
          <Cloud className="h-6 w-6 mx-auto text-neutral-300 mb-2" />
          <p className="text-[12.5px] text-neutral-500">No sync events yet.</p>
          <p className="text-[11px] text-neutral-400 mt-1">Push a lead or wait for an ARIVE callback to populate this feed.</p>
        </div>
      ) : (
        <div className="divide-y divide-neutral-100 dark:divide-neutral-900">
          {logs.map((log) => (
            <AriveLogRow key={log.id} log={log} />
          ))}
        </div>
      )}
    </div>
  );
}

function AriveLogRow({ log }) {
  const isPush = log.direction === 'push';
  const isError = log.status === 'error';
  const isSuccess = log.status === 'success';
  const tone = isError
    ? { bg: 'bg-rose-50 dark:bg-rose-950/30', border: 'border-rose-200 dark:border-rose-500/30', txt: 'text-rose-700 dark:text-rose-300', label: 'Failed' }
    : isSuccess
      ? { bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200 dark:border-emerald-500/30', txt: 'text-emerald-700 dark:text-emerald-300', label: 'Success' }
      : { bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-200 dark:border-amber-500/30', txt: 'text-amber-700 dark:text-amber-300', label: 'Queued' };
  const summary = isPush
    ? (log.payload?.name || log.payload?.reference_id || log.lead_id || 'Lead')
    : (log.payload?.arive_loan_id || log.arive_loan_id || log.lead_id || 'Update');
  return (
    <div
      className={cn(
        'px-5 py-3 flex items-start gap-3 text-[12.5px]',
        isError && 'bg-rose-50/40 dark:bg-rose-950/20 border-l-2 border-rose-400 dark:border-rose-500'
      )}
      data-testid={`arive-log-${log.id}`}
    >
      <span className={cn('inline-flex items-center justify-center h-7 w-7 rounded-md border shrink-0 mt-0.5', tone.bg, tone.border)}>
        {isPush ? <ArrowRight className="h-3.5 w-3.5 text-emerald-600" /> : <ArrowLeft className="h-3.5 w-3.5 text-sky-600" />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-neutral-900 dark:text-white truncate">
          {isPush ? 'Push → ARIVE' : 'Pull ← ARIVE'} <span className="text-neutral-400 font-normal">·</span>{' '}
          <span className="text-neutral-700 dark:text-neutral-300 font-normal">{summary}</span>
        </div>
        <div
          className={cn(
            'text-[11px] mt-0.5 whitespace-pre-wrap break-words leading-snug',
            isError
              ? 'text-rose-700 dark:text-rose-300 font-semibold'
              : 'text-neutral-500'
          )}
        >
          {log.message || '—'}
        </div>
      </div>
      <span className={cn('text-[10px] uppercase tracking-wider font-bold border rounded-full px-2 py-0.5 shrink-0 mt-0.5', tone.bg, tone.border, tone.txt)}>
        {tone.label}
      </span>
      <span className="text-[10.5px] text-neutral-400 tabular-nums shrink-0 w-20 text-right mt-0.5">{fmtAgo(log.created_at)}</span>
    </div>
  );
}

function FlowStep({ n, title, body, arrow }) {
  return (
    <div className="rounded-xl bg-white/70 dark:bg-neutral-950/40 border hairline p-3">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-neutral-500">
        <span className="h-5 w-5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 inline-flex items-center justify-center text-[10px] font-extrabold">{n}</span>
        {arrow === 'forward' ? <ArrowRight className="h-3.5 w-3.5 text-emerald-600" /> : <ArrowLeft className="h-3.5 w-3.5 text-sky-600" />}
        {title}
      </div>
      <p className="text-[12px] text-neutral-700 dark:text-neutral-300 mt-1.5 leading-snug">{body}</p>
    </div>
  );
}

function fmtAgo(iso) {
  try {
    const d = new Date(iso);
    const diff = Math.round((Date.now() - d.getTime()) / 60000);
    if (diff < 1) return 'just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.round(diff / 60)}h ago`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

function Field({ label, placeholder, value, onChange, secret, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-neutral-500">{label}</label>
      <input
        data-testid={testid}
        type={secret ? 'password' : 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full h-11 px-3 rounded-md bg-white dark:bg-neutral-900 border hairline text-[14px] font-semibold text-neutral-900 dark:text-white placeholder:font-normal placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 transition"
      />
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    not_configured: { label: 'Not configured', cls: 'bg-neutral-100 text-neutral-600 border-neutral-200' },
    configured:     { label: 'Webhook saved', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    verified:       { label: 'Live', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    error:          { label: 'Error', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  };
  const s = map[status] || map.not_configured;
  return (
    <span className={cn('inline-flex items-center text-[11px] uppercase tracking-wider font-bold border rounded-full px-2.5 py-1', s.cls)}>
      {s.label}
    </span>
  );
}
